import { Button } from "./ui/button";
import PlayingCard from "./PlayingCard";
import { Coins, MessageSquare, TrendingUp, Award } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PlayerSeat({ 
  player, 
  position, 
  isNegotiationPhase,
  onTradeClick,
  onCardClick,  // (cardIndex) => void - for toggling interest chip
  myInitial = "?",  // My initial to display on interest chips
  allPlayers = []  // All players to get interest chip data
}) {
  if (!player) return null;

  // Only show public cards
  const publicCards = player.cards?.filter(card => 
    card.is_public && card.suit !== "hidden"
  ) || [];

  // Get card layout based on position and count
  const getCardGridClass = () => {
    const cardCount = publicCards.length;
    if (cardCount <= 4) return "grid-cols-2";
    if (cardCount <= 6) return "grid-cols-3";
    if (cardCount <= 8) return "grid-cols-4";
    return "grid-cols-5";
  };

  const getMaxWidth = () => {
    if (position === "top") return "max-w-[200px]";
    if (position === "left" || position === "right") return "max-w-[100px]";
    return "max-w-[200px]";
  };

  const formatRating = (value) => {
    if (value === undefined || value === null) return "-";
    return Math.round(value);
  };

  // Get bubble position based on player position
  const getBubblePosition = () => {
    if (position === "top") return "left-full ml-2 top-1/2 -translate-y-1/2";
    return "-top-10 left-1/2 -translate-x-1/2";
  };

  const getBubbleArrow = () => {
    if (position === "top") return "absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 bg-amber-500 rotate-45";
    return "absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-amber-500 rotate-45";
  };

  // Get status label for card
  const getCardStatusLabel = (status) => {
    switch (status) {
      case 1: return "譲渡可";
      case 2: return "譲渡不可";
      default: return null;
    }
  };

  // Collect all interest chips placed on this player's cards
  // interest_chips format: { [target_player_id]: [card_indices] }
  const getInterestChipsForCard = (cardIndex) => {
    const chips = [];
    for (const otherPlayer of allPlayers) {
      if (otherPlayer.id === player.id) continue;
      const interests = otherPlayer.interest_chips?.[player.id] || [];
      if (interests.includes(cardIndex)) {
        chips.push({
          playerId: otherPlayer.id,
          initial: otherPlayer.nickname?.[0]?.toUpperCase() || "?"
        });
      }
    }
    return chips;
  };

  // Generate default avatar URL based on nickname
  const getDefaultAvatar = () => {
    const colors = ["#5865F2", "#57F287", "#FEE75C", "#EB459E", "#ED4245"];
    const colorIndex = player.id ? player.id.charCodeAt(0) % colors.length : 0;
    const initial = player.nickname?.[0]?.toUpperCase() || "?";
    return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="${encodeURIComponent(colors[colorIndex])}"/><text x="32" y="42" font-size="32" text-anchor="middle" fill="white">${encodeURIComponent(initial)}</text></svg>`;
  };

  const avatarUrl = player.avatar_url || getDefaultAvatar();

  return (
    <div 
      className="flex flex-col items-center gap-1"
      data-testid={`player-seat-${position}`}
    >
      {/* Player Info Card */}
      <div className="relative">
        {/* Chip Request Bubble - from server state */}
        <AnimatePresence>
          {player.chip_request && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`absolute ${getBubblePosition()} bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-lg whitespace-nowrap z-20`}
            >
              チップ希望
              <div className={getBubbleArrow()}></div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-card/95 backdrop-blur-sm border border-border rounded-lg px-2.5 py-1.5 min-w-[120px] max-w-[160px] shadow-lg">
          {/* Avatar and Name */}
          <div className="flex items-center gap-2 mb-1">
            <img 
              src={avatarUrl}
              alt={player.nickname}
              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex items-center justify-between flex-1 min-w-0 gap-1">
              <span className="font-medium text-xs truncate">{player.nickname}</span>
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${player.is_connected ? "bg-green-500" : "bg-red-500"}`} />
            </div>
          </div>
          
          {/* Chips */}
          <div className="flex items-center justify-between mt-0.5">
            <div className="flex items-center gap-0.5 text-brass font-data text-xs">
              <Coins className="w-3 h-3" />
              <span>{player.chips}</span>
            </div>
            {player.cards && (
              <span className="text-[10px] text-muted-foreground">
                {publicCards.length}/{player.cards.length}
              </span>
            )}
          </div>
          
          {/* Rating Info */}
          <div className="flex items-center justify-between mt-1 pt-1 border-t border-border/50 gap-1">
            <div className="flex items-center gap-0.5 text-[10px]" title="みかけレート">
              <TrendingUp className="w-2.5 h-2.5 text-blue-400 flex-shrink-0" />
              <span className="text-blue-400 font-data">{formatRating(player.apparent)}</span>
            </div>
            <div className="flex items-center gap-0.5 text-[10px]" title="商才">
              <Award className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
              <span className="text-amber-400 font-data">{formatRating(player.biz)}</span>
            </div>
          </div>
          
          {/* Trade Button */}
          {isNegotiationPhase && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onTradeClick();
              }}
              disabled={!player.is_connected}
              className="w-full mt-1 text-[10px] h-6 border-brass/30 hover:bg-brass/10"
              data-testid={`trade-btn-${player.id}`}
            >
              <MessageSquare className="w-2.5 h-2.5 mr-0.5" />
              取引
            </Button>
          )}
        </div>
      </div>

      {/* Player Cards with status and interest chips */}
      {publicCards.length > 0 && (
        <div className={`grid ${getCardGridClass()} gap-0.5 ${getMaxWidth()}`}>
          {player.cards?.map((card, originalIndex) => {
            // Only show public cards
            if (!card.is_public || card.suit === "hidden") return null;
            
            // Key is string in JSON serialization
            const status = player.card_status?.[String(originalIndex)] || player.card_status?.[originalIndex] || 0;
            const statusLabel = getCardStatusLabel(status);
            const interestChips = getInterestChipsForCard(originalIndex);
            
            return (
              <div 
                key={originalIndex} 
                className="relative cursor-pointer"
                onClick={() => onCardClick?.(originalIndex)}
              >
                <PlayingCard
                  suit={card.suit}
                  rank={card.rank}
                  isPublic={true}
                  size="xs"
                />
                
                {/* Card status overlay (from this player's settings) */}
                {statusLabel && (
                  <div className={`absolute inset-0 flex items-center justify-center rounded ${
                    status === 1 ? "bg-green-500/40" : "bg-red-500/40"
                  }`}>
                    <span className={`text-[8px] font-bold px-0.5 rounded ${
                      status === 1 ? "bg-green-600 text-white" : "bg-red-600 text-white"
                    }`}>
                      {statusLabel}
                    </span>
                  </div>
                )}
                
                {/* Interest chips from other players */}
                {interestChips.length > 0 && (
                  <div className="absolute -top-1 -right-1 flex gap-0.5">
                    {interestChips.slice(0, 3).map((chip, chipIdx) => (
                      <div 
                        key={chipIdx}
                        className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-[7px] font-bold text-white shadow-md border border-white z-10"
                        title={`${chip.initial}が興味あり`}
                      >
                        {chip.initial}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
